import { useEffect, useState } from "react";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_NAME, SITE_URL } from "@/components/base/Seo";
import type { Business, Category, DirectoryLocation } from "@/types/directory";
import {
  getCategories,
  getFeaturedOnlyBusinesses,
  getLatestBusinesses,
  getLocations,
  getSponsoredBusinesses,
} from "@/services/directoryService";
import HeroSection from "./components/HeroSection";
import PopularCategories from "./components/PopularCategories";
import SponsoredListings from "./components/SponsoredListings";
import FeaturedListings from "./components/FeaturedListings";
import DistrictsSection from "./components/DistrictsSection";
import LatestListings from "./components/LatestListings";
import Testimonials from "./components/Testimonials";
import CTASection from "./components/CTASection";

export default function Home() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<DirectoryLocation[]>([]);
  const [sponsored, setSponsored] = useState<Business[]>([]);
  const [featured, setFeatured] = useState<Business[]>([]);
  const [latest, setLatest] = useState<Business[]>([]);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;

    // Each section loads independently (Promise.allSettled) so one failing call
    // can never blank out the other sections — the Sponsored and Featured bands
    // still appear even when, for example, the latest-listings call fails.
    const load = async () => {
      const [categoriesRes, locationsRes, sponsoredRes, featuredRes, latestRes] =
        await Promise.allSettled([
          getCategories(),
          getLocations(),
          getSponsoredBusinesses(12),
          getFeaturedOnlyBusinesses(12),
          getLatestBusinesses(20),
        ]);

      if (!active) return;

      const sponsoredList = sponsoredRes.status === "fulfilled" ? sponsoredRes.value : [];
      const featuredList = featuredRes.status === "fulfilled" ? featuredRes.value : [];
      const latestList = latestRes.status === "fulfilled" ? latestRes.value : [];

      const anyListingFailed =
        sponsoredRes.status === "rejected" ||
        featuredRes.status === "rejected" ||
        latestRes.status === "rejected";
      const hasAnyListing =
        sponsoredList.length > 0 || featuredList.length > 0 || latestList.length > 0;

      setCategories(categoriesRes.status === "fulfilled" ? categoriesRes.value : []);
      setLocations(locationsRes.status === "fulfilled" ? locationsRes.value : []);
      setSponsored(sponsoredList);
      setFeatured(featuredList);
      setLatest(latestList);
      setError(
        anyListingFailed && !hasAnyListing
          ? "We could not load the latest listings right now. Please try again in a moment."
          : "",
      );
    };

    load();

    return () => {
      active = false;
    };
  }, [reloadToken]);

  const districts = locations.filter((location) => location.type === "district");
  const towns = locations.filter((location) => location.type === "town");

  return (
    <SiteLayout>
      <Seo
        title="Coastal Bizz Connect | Local Business Directory for Coastal Karnataka"
        description="Find verified local businesses across South Canara, Udupi and North Canara. Search restaurants, hotels, hospitals and services with real reviews, working hours and direct contact in one tap."
        keywords="local business directory Karnataka, Mangaluru businesses, Udupi business listings, North Canara directory, coastal Karnataka businesses"
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: SITE_NAME,
            url: SITE_URL,
            potentialAction: {
              "@type": "SearchAction",
              target: `${SITE_URL}/directory?keyword={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
            areaServed: ["South Canara", "Udupi", "North Canara"],
          },
        ]}
      />
      <HeroSection categories={categories} districts={districts} />
      {error ? (
        <section className="mx-auto w-full max-w-[1400px] px-6 pt-8">
          <div className="flex items-start justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span className="flex items-start gap-2.5">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
              {error}
            </span>
            <button
              type="button"
              onClick={() => setReloadToken((token) => token + 1)}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              Retry
            </button>
          </div>
        </section>
      ) : null}
      <SponsoredListings businesses={sponsored} />
      <FeaturedListings businesses={featured} />
      <LatestListings businesses={latest} />
      <PopularCategories categories={categories} />
      <DistrictsSection districts={districts} towns={towns} />
      <Testimonials />
      <CTASection />
    </SiteLayout>
  );
}